import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import ConsigneForm from '@/components/ConsigneForm';
import SignatureDialog from '@/components/SignatureDialog';
import FileViewer from '@/components/FileViewer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Plus, Eye, Edit, Trash2, PenTool, Folder, ShieldAlert, Flame, HeartPulse, Wrench, FolderKanban, Clock, ArrowLeft, FileText, AlertTriangle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Général', 'Sûreté', 'Incendie', 'Secours à personnes', 'Technique', 'Divers'];
// "Temporaire" est un dossier virtuel : toute consigne liée à un événement
// ponctuel (dates de début/fin) y est rangée, peu importe sa catégorie.
const FOLDERS = [...CATEGORIES, 'Temporaire'];

const categoryColors = {
  'Général': 'bg-gray-500',
  'Sûreté': 'bg-blue-500',
  'Incendie': 'bg-red-500',
  'Secours à personnes': 'bg-green-500',
  'Technique': 'bg-purple-500',
  'Divers': 'bg-yellow-500',
  'Temporaire': 'bg-indigo-500'
};

const folderIcons = {
  'Général': Folder,
  'Sûreté': ShieldAlert,
  'Incendie': Flame,
  'Secours à personnes': HeartPulse,
  'Technique': Wrench,
  'Divers': FolderKanban,
  'Temporaire': Clock
};

const isTemporary = (c) => !!(c.start_date && c.end_date);
const folderKey = (c) => isTemporary(c) ? 'Temporaire' : c.category;

const Consignes = ({ user, onLogout }) => {
  const location = useLocation();
  const highlightConsigneId = location.state?.highlightConsigneId || null;
  const [consignes, setConsignes] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [activeFolder, setActiveFolder] = useState(null);
  const [viewingConsigne, setViewingConsigne] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingConsigne, setEditingConsigne] = useState(null);
  const [signingConsigne, setSigningConsigne] = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'chef_poste';
  // Un compte "site" peut désormais signer une consigne, mais toujours au nom
  // d'un agent précis (sélectionné + code personnel) - jamais en son propre
  // nom, puisqu'il est partagé entre plusieurs agents.
  const canSign = user?.permission_overrides?.sign_consignes ?? ['admin', 'chef_poste', 'agent', 'site'].includes(user?.role);
  const isSiteAccount = user?.role === 'site';
  // Un compte de site n'a pas d'identité personnelle "ayant signé" (il signe
  // toujours au nom d'un agent) : les badges/pastilles "à signer" n'ont donc
  // pas de sens pour lui et resteraient affichés en permanence - masqués
  // uniquement pour ce rôle, sans retirer la possibilité de signer.
  const showSignatureNotifications = canSign && !isSiteAccount;

  // Signature depuis un compte de site : l'agent signataire doit être
  // sélectionné et confirmer son identité avec son code personnel avant de
  // pouvoir dessiner sa signature.
  const [signerAgents, setSignerAgents] = useState([]);
  const [signerId, setSignerId] = useState('');
  const [signerPin, setSignerPin] = useState('');
  const [signerPinVerified, setSignerPinVerified] = useState(false);
  const [signerPinChecking, setSignerPinChecking] = useState(false);
  const [signerPinError, setSignerPinError] = useState('');
  const [signerPinDialogOpen, setSignerPinDialogOpen] = useState(false);

  useEffect(() => {
    setSignerId('');
    setSignerPin('');
    setSignerPinVerified(false);
    setSignerPinError('');
    if (!isSiteAccount || !signingConsigne) { setSignerAgents([]); return; }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${signingConsigne.site_id}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSignerAgents(await res.json());
      } catch { /* silencieux */ }
    })();
    // eslint-disable-next-line
  }, [signingConsigne]);

  const verifySignerPin = async (pin, agentId) => {
    setSignerPinChecking(true);
    setSignerPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/events/verify-redacteur-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ redacteur_id: agentId, pin })
      });
      if (res.ok) {
        setSignerPinVerified(true);
        setSignerPinDialogOpen(false);
      } else {
        const err = await res.json();
        setSignerPinError(err.detail || 'Code incorrect');
        setSignerPinVerified(false);
        setSignerPin('');
      }
    } catch {
      setSignerPinError('Erreur de connexion, merci de réessayer');
      setSignerPinVerified(false);
    } finally {
      setSignerPinChecking(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedSite]);

  // Arrivée depuis l'encadré "Événement en cours" ou une notification : ouvre
  // directement le bon dossier puis la consigne visée.
  useEffect(() => {
    if (highlightConsigneId && consignes.length > 0) {
      const c = consignes.find(x => x.id === highlightConsigneId);
      if (c) {
        setActiveFolder(folderKey(c));
        setViewingConsigne(c);
      }
    }
    // eslint-disable-next-line
  }, [highlightConsigneId, consignes]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [consignesRes, sitesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/consignes${selectedSite !== 'all' ? `?site_id=${selectedSite}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/sites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (consignesRes.ok) {
        const data = await consignesRes.json();
        setConsignes(data);
      }

      if (sitesRes.ok) {
        const data = await sitesRes.json();
        setSites(data);
        // Un non-admin n'a pas la possibilité de choisir "Tous les sites" :
        // son site (unique, ou celui choisi à la connexion) est sélectionné directement.
        if (user?.role !== 'admin' && data.length > 0) {
          setSelectedSite(prev => (prev === 'all' ? data[0].id : prev));
        }
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleConsigneCreated = () => {
    setShowForm(false);
    setEditingConsigne(null);
    loadData();
    toast.success('Consigne enregistrée avec succès');
  };

  const handleDelete = async (consigneId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette consigne ?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/consignes/${consigneId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Consigne supprimée');
        if (viewingConsigne?.id === consigneId) setViewingConsigne(null);
        loadData();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleSign = async (signatureData) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND_URL}/api/consignes/${signingConsigne.id}/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signature_data: signatureData,
          ...(isSiteAccount ? { redacteur_id: signerId, redacteur_pin: signerPin } : {})
        })
      });

      if (response.ok) {
        toast.success('Consigne signée avec succès');
        const signedId = signingConsigne.id;
        setSigningConsigne(null);
        const token2 = localStorage.getItem('token');
        const refreshed = await fetch(`${BACKEND_URL}/api/consignes${selectedSite !== 'all' ? `?site_id=${selectedSite}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token2}` }
        });
        if (refreshed.ok) {
          const data = await refreshed.json();
          setConsignes(data);
          if (viewingConsigne?.id === signedId) {
            setViewingConsigne(data.find(c => c.id === signedId) || null);
          }
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la signature');
        if (isSiteAccount) {
          setSignerPin('');
          setSignerPinVerified(false);
        }
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-3 animate-fade-in" data-testid="consignes">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-1" style={{ fontFamily: 'Space Grotesk' }}>
              Dossier de Consignes
            </h1>
            <p className="text-gray-600 dark:text-slate-300">
              {canEdit ? 'Création et gestion des consignes' : 'Consultation des consignes'}
            </p>
          </div>
          {canEdit && (
            <Dialog open={showForm} onOpenChange={(open) => {
              setShowForm(open);
              if (!open) setEditingConsigne(null);
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-consigne-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle Consigne
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingConsigne ? 'Éditer' : 'Créer'} une consigne</DialogTitle>
                </DialogHeader>
                <ConsigneForm 
                  sites={sites} 
                  consigne={editingConsigne}
                  onSuccess={handleConsigneCreated} 
                  onCancel={() => {
                    setShowForm(false);
                    setEditingConsigne(null);
                  }} 
                />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filtre site */}
        <Card className="glass-effect">
          <CardContent className="pt-4 pb-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 block">Site</label>
              <Select value={selectedSite} onValueChange={(v) => { setSelectedSite(v); setActiveFolder(null); }}>
                <SelectTrigger data-testid="filter-site">
                  <SelectValue placeholder="Sélectionnez un site" />
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'admin' && <SelectItem value="all">Tous les sites</SelectItem>}
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : !activeFolder ? (
          /* ============ VUE DOSSIERS ============ */
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {FOLDERS.map((folder) => {
              const items = consignes.filter(c => folderKey(c) === folder);
              const FolderIcon = folderIcons[folder];
              const needsSignatureCount = showSignatureNotifications ? items.filter(c =>
                c.requires_signature && !c.signatures?.some(s => s.user_id === user.id)
              ).length : 0;
              return (
                <button
                  key={folder}
                  onClick={() => setActiveFolder(folder)}
                  className="card-hover glass-effect p-4 text-left relative"
                  data-testid={`folder-${folder}`}
                >
                  {needsSignatureCount > 0 && (
                    <span className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {needsSignatureCount}
                    </span>
                  )}
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-2 ${categoryColors[folder]}`}>
                    <FolderIcon className="h-5 w-5 text-white" />
                  </div>
                  <p className="font-semibold text-sm text-slate-800 dark:text-slate-100">{folder}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{items.length} consigne{items.length !== 1 ? 's' : ''}</p>
                </button>
              );
            })}
          </div>
        ) : (
          /* ============ VUE LISTE D'UN DOSSIER ============ */
          <div className="space-y-2">
            <button
              onClick={() => setActiveFolder(null)}
              className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 mb-1"
              data-testid="back-to-folders"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour aux dossiers
            </button>
            <div className="flex items-center gap-2 mb-2">
              {(() => { const Icon = folderIcons[activeFolder]; return <Icon className="h-5 w-5 text-slate-500" />; })()}
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">{activeFolder}</h2>
            </div>

            {consignes.filter(c => folderKey(c) === activeFolder).length === 0 ? (
              <Card className="glass-effect">
                <CardContent className="py-10 text-center">
                  <FileText className="h-10 w-10 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-slate-300 text-sm">Aucune consigne dans ce dossier</p>
                </CardContent>
              </Card>
            ) : (
              consignes.filter(c => folderKey(c) === activeFolder).map((consigne) => {
                const site = sites.find(s => s.id === consigne.site_id);
                const userHasSigned = consigne.signatures?.some(s => s.user_id === user.id);
                const needsSignature = showSignatureNotifications && consigne.requires_signature && !userHasSigned;
                return (
                  <button
                    key={consigne.id}
                    onClick={() => setViewingConsigne(consigne)}
                    className={`w-full text-left card-hover glass-effect p-3 flex items-center justify-between gap-2 ${needsSignature ? 'ring-2 ring-orange-400 bg-orange-50/50' : ''}`}
                    data-testid={`consigne-list-item-${consigne.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{consigne.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{site?.name || 'Site inconnu'} · Par {consigne.created_by_name}</p>
                    </div>
                    {needsSignature ? (
                      <Badge className="bg-orange-500 text-white text-xs shrink-0 animate-pulse">À signer</Badge>
                    ) : showSignatureNotifications && consigne.requires_signature ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs shrink-0">Signée ✓</Badge>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ============ POP-UP DÉTAIL D'UNE CONSIGNE ============ */}
      <Dialog open={!!viewingConsigne} onOpenChange={(open) => !open && setViewingConsigne(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {viewingConsigne && (() => {
            const consigne = viewingConsigne;
            const site = sites.find(s => s.id === consigne.site_id);
            const userHasSigned = consigne.signatures?.some(s => s.user_id === user.id);
            const needsSignature = showSignatureNotifications && consigne.requires_signature && !userHasSigned;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <Badge className={`${categoryColors[consigne.category]} text-white text-xs`}>{consigne.category}</Badge>
                    {showSignatureNotifications && consigne.requires_signature && (
                      needsSignature ? (
                        <Badge className="bg-orange-500 text-white animate-pulse text-xs">À signer</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">Signée ✓</Badge>
                      )
                    )}
                    {isTemporary(consigne) && (
                      <Badge className="bg-indigo-500 text-white text-xs">
                        Du {consigne.start_date.split('-').reverse().join('/')}{consigne.start_time ? ` ${consigne.start_time}` : ''} au {consigne.end_date.split('-').reverse().join('/')}{consigne.end_time ? ` ${consigne.end_time}` : ''}
                      </Badge>
                    )}
                  </div>
                  <DialogTitle>{consigne.title}</DialogTitle>
                  <p className="text-xs text-gray-600 dark:text-slate-300">{site?.name || 'Site inconnu'}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Par {consigne.created_by_name}</p>
                </DialogHeader>

                <div className="space-y-3">
                  <p className="text-sm text-gray-700 dark:text-slate-200 whitespace-pre-wrap">{consigne.content}</p>

                  {consigne.attachments && consigne.attachments.length > 0 && (
                    <FileViewer fileIds={consigne.attachments} />
                  )}

                  {consigne.signatures && consigne.signatures.length > 0 && (
                    <Accordion type="single" collapsible>
                      <AccordionItem value="signatures">
                        <AccordionTrigger className="text-xs">
                          Signatures ({consigne.signatures.length})
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {consigne.signatures.map((sig, idx) => (
                              <div key={idx} className="border rounded-lg p-2 bg-gray-50 dark:bg-slate-800">
                                <p className="font-medium text-sm">{sig.user_name}</p>
                                <p className="text-xs text-gray-600 dark:text-slate-300 mb-1">
                                  {new Date(sig.signed_at).toLocaleString('fr-FR')}
                                </p>
                                {sig.signature_data && (
                                  <img
                                    src={sig.signature_data}
                                    alt="Signature"
                                    className="border rounded h-16 w-full object-contain bg-white dark:bg-slate-800"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}

                  <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    {!userHasSigned && canSign && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSigningConsigne(consigne)}
                        data-testid={`sign-consigne-${consigne.id}`}
                      >
                        <PenTool className="h-4 w-4 mr-1" />
                        Signer
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingConsigne(consigne);
                            setViewingConsigne(null);
                            setShowForm(true);
                          }}
                          data-testid={`edit-consigne-${consigne.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(consigne.id)}
                          data-testid={`delete-consigne-${consigne.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Sur un compte de site, l'agent signataire doit d'abord être choisi
          et confirmer son identité avec son code personnel, avant de pouvoir
          dessiner sa signature. */}
      {signingConsigne && isSiteAccount && (
        <Dialog open={true} onOpenChange={(open) => !open && setSigningConsigne(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Qui signe cette consigne ?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Agent signataire</Label>
                <Select
                  value={signerId}
                  onValueChange={(value) => {
                    setSignerId(value);
                    setSignerPin('');
                    setSignerPinVerified(false);
                    setSignerPinError('');
                    setSignerPinDialogOpen(true);
                  }}
                >
                  <SelectTrigger data-testid="consigne-signer-select">
                    <SelectValue placeholder="Sélectionnez un agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {signerAgents
                      .filter((a) => !signingConsigne.signatures?.some((s) => s.user_id === a.id))
                      .map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {signerAgents.length > 0 && signerAgents.every((a) => signingConsigne.signatures?.some((s) => s.user_id === a.id)) && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">Tous les agents de ce site ont déjà signé cette consigne.</p>
                )}
              </div>

              {signerId && (
                <div>
                  {signerPinVerified ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                      <PenTool className="h-4 w-4 shrink-0" />
                      <span>Identité confirmée</span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => { setSignerPin(''); setSignerPinVerified(false); setSignerPinError(''); setSignerPinDialogOpen(true); }}
                      >
                        Modifier
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={() => setSignerPinDialogOpen(true)}>
                      Saisir le code personnel de l'agent *
                    </Button>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSigningConsigne(null)}>Annuler</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Popup de saisie du code personnel de l'agent signataire */}
      <Dialog open={signerPinDialogOpen} onOpenChange={(open) => { if (!signerPinChecking) setSignerPinDialogOpen(open); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-base">Code personnel</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Demandé à {signerAgents.find(a => a.id === signerId)?.name || "l'agent sélectionné"} pour confirmer son identité.
          </p>
          <div className="flex flex-col items-center gap-2 py-2">
            <InputOTP
              maxLength={4}
              inputMode="numeric"
              value={signerPin}
              onChange={(value) => { setSignerPin(value); setSignerPinError(''); }}
              onComplete={(value) => verifySignerPin(value, signerId)}
              disabled={signerPinChecking}
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} mask />
                <InputOTPSlot index={1} mask />
                <InputOTPSlot index={2} mask />
                <InputOTPSlot index={3} mask />
              </InputOTPGroup>
            </InputOTP>
            {signerPinChecking && <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>}
            {signerPinError && (
              <div className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{signerPinError}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      {signingConsigne && (!isSiteAccount || signerPinVerified) && (
        <SignatureDialog
          open={!!signingConsigne}
          onClose={() => setSigningConsigne(null)}
          onSign={handleSign}
          title={`Signer la consigne: ${signingConsigne.title}`}
        />
      )}
    </Layout>
  );
};

export default Consignes;
